/* frontend/src/pages/CatalogoDetailPage.js */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  GaugeCircle, 
  Fuel, 
  Repeat, 
  Palette, 
  ArrowLeft,
  Car,
  MessageCircle,
  Mail,
  Share2,
  Check
} from 'lucide-react';
import api from '../api';

// Shadcn UI components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Typography } from '../components/ui/typography';
import { Spinner } from '../components/ui/spinner';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "../components/ui/sheet";

// Components
import ContactForm from '../components/ContactForm';

export default function CatalogoDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("fotos");
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [shareURL, setShareURL] = useState('');

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/public/catalogo/${id}`);
        setVehicle(response.data);
        setShareURL(window.location.href);
      } catch (error) {
        console.error('Erro ao carregar detalhes do veículo:', error);
        setError(t('catalogoDetail.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  // Função para formatar valor monetário
  const formatCurrency = (value) => {
    if (!value) return t('catalogoDetail.pricing.onRequest');
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Monta URL absoluta para fotos/vídeos
  const getUrl = (src) => {
    if (!src) return null;
    return src.startsWith('http')
      ? src
      : `${api.defaults.baseURL.replace('/api', '')}${src}`;
  };

  // Função para compartilhar o veículo
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vehicle.infoTecnica?.marca} ${vehicle.infoTecnica?.modelo}`,
          text: `Confira este ${vehicle.infoTecnica?.marca} ${vehicle.infoTecnica?.modelo} ${vehicle.infoTecnica?.anoFabricacao}/${vehicle.infoTecnica?.anoModelo} no catálogo de seminovos!`,
          url: shareURL
        });
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
      }
    } else {
      // Fallback para navegadores que não suportam API de compartilhamento
      navigator.clipboard.writeText(shareURL);
      alert(t('catalogoDetail.actions.linkCopied'));
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{t('catalogoDetail.loading.title')}</title>
          <meta name="description" content={t('catalogoDetail.loading.description')} />
        </Helmet>
        <div className="container mx-auto px-4 py-12 min-h-[60vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Spinner className="h-10 w-10 text-primary" />
            <p className="text-muted-foreground">{t('catalogoDetail.loading.message')}</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !vehicle) {
    return (
      <>
        <Helmet>
          <title>{t('catalogoDetail.errors.title')}</title>
          <meta name="description" content={t('catalogoDetail.errors.metaDescription')} />
        </Helmet>
        <div className="container mx-auto px-4 py-12 min-h-[60vh] flex items-center justify-center">
          <div className="max-w-md text-center">
            <Car className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <Typography variant="h3" className="mb-2">{t('catalogoDetail.errors.notFound')}</Typography>
            <p className="text-muted-foreground mb-6">
              {error || t('catalogoDetail.errors.notAvailable')}
            </p>
            <Button asChild>
              <Link to="/catalogo">{t('catalogoDetail.actions.backToCatalog')}</Link>
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Array de fotos ou array vazio se não houver fotos
  const fotos = vehicle.midia?.fotos || [];
  const videos = vehicle.midia?.videos || [];

  // Prepare SEO tags
  const vehicleName = `${vehicle.infoTecnica?.marca || ''} ${vehicle.infoTecnica?.modelo || ''} ${vehicle.infoTecnica?.versao || ''}`.trim();
  const vehicleDescription = `${vehicle.infoTecnica?.anoFabricacao || ''}/${vehicle.infoTecnica?.anoModelo || ''} - ${vehicle.infoTecnica?.km?.toLocaleString('pt-BR') || '0'} km - ${vehicle.infoTecnica?.combustivel || ''} - ${formatCurrency(vehicle.comercial?.valorVenda)}`;

  // Lista de características do veículo
  const características = [
    { title: t('catalogoDetail.fields.engine'), value: vehicle.infoTecnica?.motor || t('catalogoDetail.notInformed') },
    { title: t('catalogoDetail.fields.power'), value: vehicle.infoTecnica?.potencia || t('catalogoDetail.notInformed') },
    { title: t('catalogoDetail.fields.doors'), value: vehicle.infoTecnica?.portas || t('catalogoDetail.notInformed') },
    { title: t('catalogoDetail.fields.plateEnding'), value: vehicle.placa?.slice(-1) || t('catalogoDetail.notInformed') }
  ];

  // Lista de itens opcionais
  const opcionais = vehicle.opcionais?.itens || [];

  return (
    <>
      <Helmet>
        <title>{vehicleName} | Seminovos</title>
        <meta name="description" content={`${vehicleName} ${vehicleDescription}. Veículo seminovo com garantia de procedência e qualidade.`} />
        <meta name="keywords" content={`${vehicle.infoTecnica?.marca}, ${vehicle.infoTecnica?.modelo}, seminovos, carros usados, ${vehicle.infoTecnica?.combustivel}`} />
        <meta property="og:title" content={vehicleName} />
        <meta property="og:description" content={`${vehicleName} ${vehicleDescription}. Veículo seminovo com garantia de procedência e qualidade.`} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={fotos.length > 0 ? getUrl(fotos[0]) : '/placeholder-car.jpg'} />
        <meta property="product:price:amount" content={vehicle.comercial?.valorVenda || 0} />
        <meta property="product:price:currency" content="BRL" />
        <link rel="canonical" href={`/catalogo/${vehicle._id}`} />
      </Helmet>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Botão de voltar */}
        <div className="mb-6">
          <Button variant="ghost" asChild className="pl-0 hover:pl-0">
            <Link to="/catalogo" className="flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" />
              {t('catalogoDetail.actions.backToCatalog')}
            </Link>
          </Button>
        </div>

        {/* Cabeçalho/Título */}
        <div className="mb-8">
          <Typography variant="h2" className="text-3xl font-bold mb-2">
            {vehicle.infoTecnica?.marca} {vehicle.infoTecnica?.modelo} {vehicle.infoTecnica?.versao}
          </Typography>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>{vehicle.infoTecnica?.anoFabricacao}/{vehicle.infoTecnica?.anoModelo}</span>
            </span>
            <span className="flex items-center gap-1">
              <GaugeCircle className="h-4 w-4" />
              <span>{vehicle.infoTecnica?.km?.toLocaleString('pt-BR') || '0'} km</span>
            </span>
            <span className="flex items-center gap-1">
              <Fuel className="h-4 w-4" />
              <span>{vehicle.infoTecnica?.combustivel || 'Não informado'}</span>
            </span>
            <span className="flex items-center gap-1">
              <Repeat className="h-4 w-4" />
              <span>{vehicle.infoTecnica?.cambio || 'Não informado'}</span>
            </span>
            <span className="flex items-center gap-1">
              <Palette className="h-4 w-4" />
              <span>{vehicle.infoTecnica?.cor || 'Não informada'}</span>
            </span>
          </div>
        </div>

        {/* Layout principal: 2 colunas em desktop, 1 coluna em mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Coluna de mídia (fotos e vídeos) */}
          <div className="lg:col-span-2">
            <Card>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="px-4 pt-4">
                  <TabsList className="w-full grid grid-cols-2">
                    <TabsTrigger value="fotos">{t('catalogoDetail.tabs.photos')} ({fotos.length})</TabsTrigger>
                    <TabsTrigger value="videos">{t('catalogoDetail.tabs.videos')} ({videos.length})</TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="fotos">
                  <div>
                    {fotos.length > 0 ? (
                      <div className="space-y-4 p-4">
                        {/* Imagem principal */}
                        <div className="relative aspect-video bg-muted rounded-md overflow-hidden">
                          <img
                            src={getUrl(fotos[activeImageIndex])}
                            alt={`${vehicle.infoTecnica?.marca} ${vehicle.infoTecnica?.modelo}`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.src = '/placeholder-car.jpg';
                            }}
                          />
                        </div>
                        
                        {/* Miniaturas */}
                        {fotos.length > 1 && (
                          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {fotos.map((foto, index) => (
                              <div 
                                key={index}
                                className={`aspect-square bg-muted rounded-md cursor-pointer overflow-hidden 
                                  ${activeImageIndex === index ? 'ring-2 ring-primary' : 'opacity-70'}`}
                                onClick={() => setActiveImageIndex(index)}
                              >
                                <img
                                  src={getUrl(foto)}
                                  alt={`${vehicle.infoTecnica?.marca} ${vehicle.infoTecnica?.modelo} - Foto ${index + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = '/placeholder-car.jpg';
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="min-h-[300px] flex flex-col items-center justify-center p-8 text-center">
                        <Car className="h-16 w-16 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">
                          {t('catalogoDetail.media.noPhotos')}
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="videos">
                  <div>
                    {videos.length > 0 ? (
                      <div className="p-4 space-y-4">
                        {videos.map((video, index) => (
                          <div key={index} className="aspect-video bg-muted rounded-md overflow-hidden">
                            <video
                              controls
                              className="w-full h-full"
                            >
                              <source src={getUrl(video)} type="video/mp4" />
                              {t('catalogoDetail.media.videoNotSupported')}
                            </video>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="min-h-[300px] flex flex-col items-center justify-center p-8 text-center">
                        <Car className="h-16 w-16 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">
                          {t('catalogoDetail.media.noVideos')}
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </Card>

            {/* Informações técnicas do veículo */}
            <Card className="mt-6 p-6">
              <Typography variant="h3" className="font-semibold mb-4">
                {t('catalogoDetail.sections.vehicleInfo')}
              </Typography>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.license')}</span>
                  <span>{vehicle.placa ? `${vehicle.placa.slice(0, 3)}****` : t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.brand')}</span>
                  <span>{vehicle.infoTecnica?.marca || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.model')}</span>
                  <span>{vehicle.infoTecnica?.modelo || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.version')}</span>
                  <span>{vehicle.infoTecnica?.versao || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.manufacturingYear')}</span>
                  <span>{vehicle.infoTecnica?.anoFabricacao || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.modelYear')}</span>
                  <span>{vehicle.infoTecnica?.anoModelo || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.color')}</span>
                  <span>{vehicle.infoTecnica?.cor || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.fuel')}</span>
                  <span>{vehicle.infoTecnica?.combustivel || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.transmission')}</span>
                  <span>{vehicle.infoTecnica?.cambio || t('catalogoDetail.notInformed')}</span>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">{t('catalogoDetail.fields.mileage')}</span>
                  <span>{vehicle.infoTecnica?.km?.toLocaleString('pt-BR') || '0'} km</span>
                </div>

                {características.map((item, index) => (
                  <div key={index} className="flex flex-col">
                    <span className="text-muted-foreground text-sm">{item.title}</span>
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Opcionais */}
              {opcionais.length > 0 && (
                <>
                  <Separator className="my-6" />
                  <Typography variant="h4" className="font-semibold mb-4">
                    {t('catalogoDetail.sections.optionals')}
                  </Typography>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {opcionais.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </div>
          
          {/* Coluna de preço e contato */}
          <div>
            <Card className="p-6">
              <div className="mb-6">
                <span className="text-sm text-muted-foreground">{t('catalogoDetail.pricing.value')}</span>
                <Typography variant="h3" className="text-3xl font-bold text-primary">
                  {formatCurrency(vehicle.comercial?.valorVenda)}
                </Typography>
                
                {vehicle.comercial?.valorFipe && (
                  <div className="mt-2">
                    <span className="text-sm text-muted-foreground">{t('catalogoDetail.pricing.fipeTable')}: </span>
                    <span>{formatCurrency(vehicle.comercial?.valorFipe)}</span>
                  </div>
                )}

                <div className="flex justify-end mt-2">
                  <Button variant="ghost" size="sm" onClick={handleShare} className="flex items-center gap-1">
                    <Share2 className="h-4 w-4" />
                    {t('catalogoDetail.actions.share')}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-4">
                <Typography variant="h4" className="font-medium">
                  {t('catalogoDetail.contact.interested')}
                </Typography>
                
                <Button className="w-full flex items-center gap-2" size="lg" asChild>
                  <a 
                    href={`https://wa.me/5511917530290?text=${encodeURIComponent(
                      `Olá, vi o ${vehicle.infoTecnica?.marca} ${vehicle.infoTecnica?.modelo} ${vehicle.infoTecnica?.versao} ${vehicle.infoTecnica?.anoFabricacao}/${vehicle.infoTecnica?.anoModelo} no catálogo de veículos e estou interessado`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>WhatsApp</span>
                  </a>
                </Button>
                
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="w-full flex items-center gap-2" size="lg">
                      <Mail className="h-4 w-4" />
                      <span>{t('catalogoDetail.contact.sendMessage')}</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="w-full sm:max-w-md">
                    <SheetHeader>
                      <SheetTitle>{t('catalogoDetail.contact.getInTouch')}</SheetTitle>
                      <SheetDescription>
                        {t('catalogoDetail.contact.formDescription')}
                      </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6">
                      <ContactForm 
                        inline 
                        vehicleId={vehicle._id}
                        vehicleInfo={`${vehicle.infoTecnica?.marca} ${vehicle.infoTecnica?.modelo} ${vehicle.infoTecnica?.versao} ${vehicle.infoTecnica?.anoFabricacao}/${vehicle.infoTecnica?.anoModelo}`}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  {t('catalogoDetail.info.code')}: #{vehicle._id.substring(vehicle._id.length - 6).toUpperCase()}
                </p>

                <p className="text-xs text-muted-foreground">
                  {t('catalogoDetail.info.disclaimer')}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
